import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Tooltip,
  Pagination
} from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import ShareIcon from "@mui/icons-material/Share";
import axiosInstance from "API/axiosInstance";
import { decryptText } from "config/encryption";
import { API_ENDPOINTS, API_BASE_URL } from "config/api";
import MDButton from "components/MDButton";
import signalRManager from "services/signalRManager";

// Get staffId from localStorage (token is handled by axiosInstance)
const getAdminSession = () => {
  try {
    const session = localStorage.getItem("adminLogsessiondetails") || localStorage.getItem("adminsessiondetails");
    if (session) {
      const decrypted = decryptText(session);
      if (decrypted) {
        const parsed = JSON.parse(decrypted);
        return {
          staffId: parsed.StaffId || parsed.staffId || parsed.StaffCode || parsed.staffCode || null
        };
      }
    }
  } catch (error) {
    console.error("Error extracting admin session:", error);
  }
  return { staffId: null };
};

const AdminEventManager = ({ onClose }) => {
  const { staffId } = getAdminSession();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState({
    eventId: 0,
    chapterId: "",
    eventName: "",
    eventDate: "",
    venue: "",
    description: "",
    createdUserId: 1
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, eventId: null });
  const [page, setPage] = useState(1);
  const cardsPerPage = 2;
  const [chapterOptions, setChapterOptions] = useState([]);
  const [uploadDialog, setUploadDialog] = useState({ open: false, eventId: null, eventName: "" });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [eventMedia, setEventMedia] = useState({}); // { eventId: [media array] }
  const [viewerDialog, setViewerDialog] = useState({ open: false, media: [], currentIndex: 0, eventId: null });
  const [deleteMediaDialog, setDeleteMediaDialog] = useState({ open: false, mediaId: null, eventId: null });

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_CHAPTER_EVENTS_ADMIN);
      setEvents(res.data);
    } catch (err) {
      console.error("Error fetching events:", err);
      setSnackbar({ open: true, message: "Failed to fetch events", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // Fetch chapter dropdown
    const fetchChapters = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.ADMIN_CHAPTER_DROPDOWN_DETAILS);
        setChapterOptions(res.data.chapters || []);
      } catch (error) {
        console.error("Error fetching chapters:", error);
      }
    };
    fetchChapters();
  }, []);

  useEffect(() => {
    const totalPages = Math.ceil(events.length / cardsPerPage);
    if (page > totalPages) {
      setPage(totalPages || 1);
    } else if (page < 1) {
      setPage(1);
    }
  }, [events, page]);

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleDialogOpen = (event = null) => {
    setEditEvent(event);
    setForm(event ? { ...event } : {
      eventId: 0,
      chapterId: "",
      eventName: "",
      eventDate: "",
      venue: "",
      description: "",
      createdUserId: staffId
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditEvent(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, createdUserId: staffId };
      const res = await axiosInstance.post(API_ENDPOINTS.ADMIN.MERGE_CHAPTER_EVENT, payload);
      setSnackbar({ open: true, message: res.data.message || "Saved successfully", severity: "success" });
      setDialogOpen(false);
      fetchEvents();
    } catch (err) {
      console.error("Error saving event:", err);
      setSnackbar({ open: true, message: "Failed to save event", severity: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.ADMIN.DELETE_CHAPTER_EVENT, {
        eventId: deleteDialog.eventId,
        deletedBy: staffId
      });
      setSnackbar({ open: true, message: res.data.message || "Deleted successfully", severity: "success" });
      setDeleteDialog({ open: false, eventId: null });
      fetchEvents();
    } catch (err) {
      console.error("Error deleting event:", err);
      setSnackbar({ open: true, message: "Failed to delete event", severity: "error" });
    }
  };

  const fetchEventMedia = async (eventId) => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.ADMIN.GET_EVENT_MEDIA(eventId));
      setEventMedia(prev => ({ ...prev, [eventId]: res.data || [] }));
      return res.data || [];
    } catch (err) {
      console.error("Error fetching event media:", err);
      return [];
    }
  };

  const handleUploadClick = (event) => {
    setUploadDialog({ open: true, eventId: event.eventId, eventName: event.eventName });
    setSelectedFiles([]);
    setUploadProgress({ current: 0, total: 0 });
    // Fetch existing media for this event
    fetchEventMedia(event.eventId);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const maxImageSize = 50 * 1024 * 1024; // 50MB
    const maxVideoSize = 500 * 1024 * 1024; // 500MB
    const validFiles = [];
    const rejected = [];

    files.forEach((file) => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) {
        rejected.push(`${file.name} (unsupported type)`);
        return;
      }

      if (isImage && file.size > maxImageSize) {
        rejected.push(`${file.name} (image > 50MB)`);
        return;
      }

      if (isVideo && file.size > maxVideoSize) {
        rejected.push(`${file.name} (video > 500MB)`);
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length) {
      setSelectedFiles((prev) => {
        const existing = new Set(prev.map((f) => `${f.name}_${f.size}_${f.lastModified}`));
        const merged = [...prev];
        validFiles.forEach((file) => {
          const key = `${file.name}_${file.size}_${file.lastModified}`;
          if (!existing.has(key)) {
            merged.push(file);
            existing.add(key);
          }
        });
        return merged;
      });
    }

    if (rejected.length) {
      setSnackbar({
        open: true,
        message: `Skipped ${rejected.length} file(s): ${rejected.slice(0, 2).join(", ")}${rejected.length > 2 ? "..." : ""}`,
        severity: "warning"
      });
    }

    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || !uploadDialog.eventId) {
      setSnackbar({ open: true, message: "Please select at least one file", severity: "error" });
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: selectedFiles.length });
    try {
      let successCount = 0;
      let failedCount = 0;

      for (let idx = 0; idx < selectedFiles.length; idx += 1) {
        const file = selectedFiles[idx];
        setUploadProgress({ current: idx + 1, total: selectedFiles.length });
        const formData = new FormData();
        formData.append('file', file);

        try {
          await axiosInstance.post(
            API_ENDPOINTS.ADMIN.UPLOAD_EVENT_MEDIA(uploadDialog.eventId, staffId),
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          successCount += 1;
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          failedCount += 1;
        }
      }

      setSnackbar({
        open: true,
        message: `Uploaded ${successCount}/${selectedFiles.length} file(s)${failedCount ? `, failed ${failedCount}` : ""}.`,
        severity: failedCount ? "warning" : "success"
      });
      setUploadDialog({ open: false, eventId: null, eventName: "" });
      setSelectedFiles([]);
      setUploadProgress({ current: 0, total: 0 });
      
      // Refresh media list for this event
      await fetchEventMedia(uploadDialog.eventId);
    } catch (err) {
      console.error("Error uploading media:", err);
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to upload media", severity: "error" });
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleViewMedia = async (event) => {
    const media = await fetchEventMedia(event.eventId);
    if (media && media.length > 0) {
      setViewerDialog({ open: true, media: media, currentIndex: 0, eventId: event.eventId });
    } else {
      setSnackbar({ open: true, message: "No media found for this event", severity: "info" });
    }
  };

  const handleCloseViewer = () => {
    setViewerDialog({ open: false, media: [], currentIndex: 0, eventId: null });
  };

  const handleNextMedia = () => {
    setViewerDialog(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.media.length
    }));
  };

  const handlePrevMedia = () => {
    setViewerDialog(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.media.length) % prev.media.length
    }));
  };

  const getMediaUrl = (filePath) => {
    // Construct full URL using API base URL
    return `${API_BASE_URL}${filePath}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDisplayFileName = (media) => media?.fileName || media?.originalFileName || "media";

  const handleDeleteMedia = async () => {
    if (!deleteMediaDialog.mediaId || !deleteMediaDialog.eventId) return;

    const eventId = deleteMediaDialog.eventId;
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.ADMIN.DELETE_EVENT_MEDIA, {
        mediaId: deleteMediaDialog.mediaId,
        deletedBy: staffId
      });
      setSnackbar({ open: true, message: res.data.message || "Media deleted successfully", severity: "success" });
      setDeleteMediaDialog({ open: false, mediaId: null, eventId: null });
      // Refresh media list for the event
      await fetchEventMedia(eventId);
    } catch (err) {
      console.error("Error deleting media:", err);
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to delete media", severity: "error" });
    }
  };

  const handleShareToGlobalChat = async (media, eventId) => {
    try {
      // Find the event details
      const event = events.find(e => e.eventId === eventId);
      if (!event) {
        setSnackbar({ open: true, message: "Event not found", severity: "error" });
        return;
      }

      // Construct event details message
      let eventDetails = `🎉 **Event: ${event.eventName}**\n\n`;
      if (event.description) {
        eventDetails += `${event.description}\n\n`;
      }
      if (event.venue) {
        eventDetails += `📍 Venue: ${event.venue}\n`;
      }
      if (event.eventDate) {
        eventDetails += `📅 Date: ${new Date(event.eventDate).toLocaleDateString()}\n`;
      }
      if (event.chapterName) {
        eventDetails += `👥 Chapter: ${event.chapterName}\n`;
      }

      // Get the media URL
      const mediaUrl = getMediaUrl(media.filePath);

      // Check if SignalR is connected
      const isConnected = signalRManager.getIsConnected();
      if (!isConnected) {
        // Try to start connection
        await signalRManager.startConnection();
      }

      // Send message to global chat
      // For both images and videos, use imageUrl (backend supports ImageUrl, and frontend can handle videos from ImageUrl)
      const imageUrl = mediaUrl;
      const audioUrl = null;
      
      const messageText = eventDetails;

      const success = await signalRManager.sendMessage(messageText, imageUrl, audioUrl);
      
      if (success) {
        setSnackbar({ open: true, message: "Media shared to global chat successfully!", severity: "success" });
      } else {
        setSnackbar({ open: true, message: "Failed to share to global chat. Please try again.", severity: "error" });
      }
    } catch (err) {
      console.error("Error sharing to global chat:", err);
      setSnackbar({ open: true, message: "Failed to share to global chat. Please ensure you are connected.", severity: "error" });
    }
  };

  return (
    <Box sx={{ width: 400, maxWidth: '100vw', p: 3, position: 'relative', bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Tooltip title="Close">
          <IconButton onClick={onClose} color="error">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="h5" fontWeight="bold" mb={2}>Manage Events</Typography>
      <MDButton color="info" variant="gradient" startIcon={<AddIcon />} onClick={() => handleDialogOpen()} sx={{ mb: 2 }}>
        Add Event
      </MDButton>
      {loading ? <CircularProgress /> : (
        <>
          <Grid container spacing={3} justifyContent="center">
            {events.slice((page-1)*cardsPerPage, page*cardsPerPage).map((event) => (
              <Grid item xs={12} key={event.eventId}>
                <Card sx={{ width: '100%', borderRadius: 3, boxShadow: 3, bgcolor: '#fff', minHeight: 220, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', transition: 'all 0.3s ease', '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' } }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <EventIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight="bold">{event.eventName}</Typography>
                    </Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                      {event.eventDate ? new Date(event.eventDate).toLocaleDateString() : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>{event.description}</Typography>
                    <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                      <Chip label={event.chapterName} size="small" color="info" />
                      <Chip label={event.venue} size="small" icon={<PlaceIcon />} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Created: {event.createdAt ? new Date(event.createdAt).toLocaleString() : ''}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                    <Box>
                      <Tooltip title="Upload Photo/Video">
                        <IconButton onClick={() => handleUploadClick(event)} color="primary">
                          <CloudUploadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Media">
                        <IconButton onClick={() => handleViewMedia(event)} color="success">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleDialogOpen(event)} color="info">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => setDeleteDialog({ open: true, eventId: event.eventId })}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={Math.ceil(events.length / cardsPerPage)}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="info"
              shape="rounded"
            />
          </Box>
        </>
      )}
      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>{editEvent ? 'Edit Event' : 'Add Event'}</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Event Name" name="eventName" value={form.eventName} onChange={handleFormChange} fullWidth />
          <TextField margin="dense" label="Event Date" name="eventDate" type="date" value={form.eventDate ? form.eventDate.substring(0,10) : ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} />
          <TextField margin="dense" label="Venue" name="venue" value={form.venue} onChange={handleFormChange} fullWidth />
          <TextField margin="dense" label="Description" name="description" value={form.description} onChange={handleFormChange} fullWidth />
          <Autocomplete
            options={chapterOptions}
            getOptionLabel={(option) => option.txt || ''}
            value={chapterOptions.find((ch) => ch.id === form.chapterId) || null}
            onChange={(_, value) => setForm((prev) => ({ ...prev, chapterId: value ? value.id : '' }))}
            renderInput={(params) => (
              <TextField {...params} label="Chapter" margin="dense" fullWidth />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleDialogClose} color="info" variant="outlined">Cancel</MDButton>
          <MDButton onClick={handleSave} color="info" variant="gradient" startIcon={<SaveIcon />}>Save</MDButton>
        </DialogActions>
      </Dialog>
      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, eventId: null })}>
        <DialogTitle>Delete Event?</DialogTitle>
        <DialogContent>Are you sure you want to delete this event?</DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteDialog({ open: false, eventId: null })} color="info" variant="outlined">Cancel</MDButton>
          <MDButton onClick={handleDelete} color="error" variant="contained">Delete</MDButton>
        </DialogActions>
      </Dialog>
      {/* Upload Dialog */}
      <Dialog open={uploadDialog.open} onClose={() => setUploadDialog({ open: false, eventId: null, eventName: "" })} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Upload Media: {uploadDialog.eventName}</Typography>
          <IconButton onClick={() => setUploadDialog({ open: false, eventId: null, eventName: "" })} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 3 }}>
            <input
              accept="image/*,video/*"
              style={{ display: 'none' }}
              id="upload-file-input"
              type="file"
              multiple
              onChange={handleFileSelect}
            />
            <label htmlFor="upload-file-input">
              <MDButton
                variant="outlined"
                component="span"
                color="info"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Select Files (Images or Videos)
              </MDButton>
            </label>
            {selectedFiles.length > 0 && (
              <Card sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Selected Files ({selectedFiles.length}):
                </Typography>
                <Box sx={{ maxHeight: 160, overflowY: 'auto', mt: 1 }}>
                  {selectedFiles.map((file) => (
                    <Box key={`${file.name}_${file.lastModified}`} display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" color="text.primary" sx={{ mr: 1, wordBreak: 'break-all' }}>
                        {file.name}
                      </Typography>
                      <Box display="flex" gap={1}>
                        <Chip
                          label={file.type.startsWith('image/') ? 'Image' : 'Video'}
                          size="small"
                          color={file.type.startsWith('image/') ? 'info' : 'primary'}
                        />
                        <Chip
                          label={formatFileSize(file.size)}
                          size="small"
                          color="default"
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            setSelectedFiles((prev) =>
                              prev.filter((f) => `${f.name}_${f.size}_${f.lastModified}` !== `${file.name}_${file.size}_${file.lastModified}`)
                            )
                          }
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Card>
            )}
          </Box>

          {/* Existing Media List */}
          {eventMedia[uploadDialog.eventId] && eventMedia[uploadDialog.eventId].length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Uploaded Media ({eventMedia[uploadDialog.eventId]?.length || 0})
              </Typography>
              <Box sx={{ maxHeight: 400, overflowY: 'auto', mt: 2 }}>
                {eventMedia[uploadDialog.eventId].map((media) => (
                  <Card 
                    key={media.mediaId} 
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      borderRadius: 2, 
                      boxShadow: 2,
                      '&:hover': { boxShadow: 4 }
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box display="flex" gap={2} flex={1}>
                        {/* Thumbnail */}
                        <Box sx={{ width: 80, height: 80, borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                          {media.fileType === 'Image' ? (
                            <img
                              src={getMediaUrl(media.filePath)}
                              alt={getDisplayFileName(media)}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <Box sx={{ width: '100%', height: '100%', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <VideoLibraryIcon color="primary" />
                            </Box>
                          )}
                        </Box>
                        
                        {/* Details */}
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight="bold" gutterBottom>
                            {getDisplayFileName(media)}
                          </Typography>
                          <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                            <Chip 
                              label={media.fileType} 
                              size="small" 
                              color={media.fileType === 'Image' ? 'info' : 'primary'}
                              icon={media.fileType === 'Image' ? <ImageIcon /> : <VideoLibraryIcon />}
                            />
                            <Chip label={formatFileSize(media.fileSize)} size="small" color="default" />
                            <Chip 
                              label={new Date(media.createdAt).toLocaleDateString()} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      </Box>

                      {/* Actions */}
                      <Box display="flex" gap={1}>
                        <Tooltip title="View">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => {
                              setViewerDialog({ 
                                open: true, 
                                media: eventMedia[uploadDialog.eventId], 
                                currentIndex: eventMedia[uploadDialog.eventId].findIndex(m => m.mediaId === media.mediaId),
                                eventId: uploadDialog.eventId
                              });
                            }}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Share to Global Chat">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleShareToGlobalChat(media, uploadDialog.eventId)}
                          >
                            <ShareIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => setDeleteMediaDialog({ open: true, mediaId: media.mediaId, eventId: uploadDialog.eventId })}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={() => setUploadDialog({ open: false, eventId: null, eventName: "" })} color="info" variant="outlined">
            Close
          </MDButton>
          <MDButton 
            onClick={handleUpload} 
            color="info" 
            variant="gradient" 
            disabled={!selectedFiles.length || uploading}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
          >
            {uploading
              ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
              : `Upload${selectedFiles.length ? ` (${selectedFiles.length})` : ''}`}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Media Viewer Dialog */}
      <Dialog 
        open={viewerDialog.open} 
        onClose={handleCloseViewer} 
        fullWidth 
        maxWidth="lg"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.12)'
        }}>
          <Box>
            <Typography variant="h6" color="text.primary">
              {viewerDialog.media.length > 0 && getDisplayFileName(viewerDialog.media[viewerDialog.currentIndex])}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {viewerDialog.currentIndex + 1} of {viewerDialog.media.length}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseViewer} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          position: 'relative', 
          bgcolor: '#f8f9fa', 
          minHeight: 500, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          {viewerDialog.media.length > 0 && (
            <>
              {viewerDialog.media.length > 1 && (
                <IconButton
                  onClick={handlePrevMedia}
                  sx={{
                    position: 'absolute',
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    zIndex: 10,
                    '&:hover': { 
                      bgcolor: 'rgba(0,0,0,0.8)',
                      boxShadow: 3
                    }
                  }}
                >
                  <Typography variant="h4">‹</Typography>
                </IconButton>
              )}
              <Box sx={{ 
                width: '100%', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                p: 3
              }}>
                <Card sx={{ 
                  bgcolor: '#fff',
                  borderRadius: 2,
                  boxShadow: 3,
                  p: 2,
                  maxWidth: '100%'
                }}>
                  {viewerDialog.media[viewerDialog.currentIndex].fileType === 'Image' ? (
                    <img
                      src={getMediaUrl(viewerDialog.media[viewerDialog.currentIndex].filePath)}
                      alt={getDisplayFileName(viewerDialog.media[viewerDialog.currentIndex])}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        borderRadius: 4
                      }}
                    />
                  ) : (
                    <video
                      src={getMediaUrl(viewerDialog.media[viewerDialog.currentIndex].filePath)}
                      controls
                      style={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        borderRadius: 4
                      }}
                    />
                  )}
                </Card>
              </Box>
              {viewerDialog.media.length > 1 && (
                <IconButton
                  onClick={handleNextMedia}
                  sx={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    zIndex: 10,
                    '&:hover': { 
                      bgcolor: 'rgba(0,0,0,0.8)',
                      boxShadow: 3
                    }
                  }}
                >
                  <Typography variant="h4">›</Typography>
                </IconButton>
              )}
              
              {/* Media Details & Thumbnails */}
              <Box sx={{ 
                position: 'absolute', 
                bottom: 0, 
                left: 0, 
                right: 0,
                bgcolor: '#fff',
                borderTop: '1px solid rgba(0,0,0,0.12)',
                p: 2
              }}>
                {/* Current Media Details */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip 
                      label={viewerDialog.media[viewerDialog.currentIndex].fileType} 
                      size="small" 
                      color={viewerDialog.media[viewerDialog.currentIndex].fileType === 'Image' ? 'info' : 'primary'}
                    />
                    <Chip 
                      label={formatFileSize(viewerDialog.media[viewerDialog.currentIndex].fileSize)} 
                      size="small" 
                      color="default"
                    />
                    <Chip 
                      label={new Date(viewerDialog.media[viewerDialog.currentIndex].createdAt).toLocaleDateString()} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                  <Box display="flex" gap={1}>
                    <MDButton
                      size="small"
                      color="success"
                      variant="outlined"
                      startIcon={<ShareIcon />}
                      onClick={() => {
                        const currentMedia = viewerDialog.media[viewerDialog.currentIndex];
                        handleShareToGlobalChat(currentMedia, viewerDialog.eventId);
                      }}
                    >
                      Share to Chat
                    </MDButton>
                    <MDButton
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        const currentMedia = viewerDialog.media[viewerDialog.currentIndex];
                        setViewerDialog({ open: false, media: [], currentIndex: 0, eventId: null });
                        setDeleteMediaDialog({ 
                          open: true, 
                          mediaId: currentMedia.mediaId, 
                          eventId: viewerDialog.eventId 
                        });
                      }}
                    >
                      Delete
                    </MDButton>
                  </Box>
                </Box>
                
                {/* Thumbnail Strip */}
                {viewerDialog.media.length > 1 && (
                  <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                    {viewerDialog.media.map((media, index) => (
                      <Box
                        key={media.mediaId}
                        onClick={() => setViewerDialog(prev => ({ ...prev, currentIndex: index }))}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          border: index === viewerDialog.currentIndex ? '3px solid' : '2px solid transparent',
                          borderColor: index === viewerDialog.currentIndex ? 'primary.main' : 'transparent',
                          opacity: index === viewerDialog.currentIndex ? 1 : 0.7,
                          transition: 'all 0.2s',
                          flexShrink: 0,
                          '&:hover': { 
                            opacity: 1,
                            transform: 'scale(1.05)',
                            boxShadow: 2
                          }
                        }}
                      >
                        {media.fileType === 'Image' ? (
                          <img
                            src={getMediaUrl(media.filePath)}
                            alt={getDisplayFileName(media)}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Box sx={{ 
                            width: '100%', 
                            height: '100%', 
                            bgcolor: '#e3f2fd', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                          }}>
                            <VideoLibraryIcon color="primary" />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Media Dialog */}
      <Dialog open={deleteMediaDialog.open} onClose={() => setDeleteMediaDialog({ open: false, mediaId: null, eventId: null })}>
        <DialogTitle>Delete Media?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this media file? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteMediaDialog({ open: false, mediaId: null, eventId: null })} color="info" variant="outlined">
            Cancel
          </MDButton>
          <MDButton onClick={handleDeleteMedia} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

AdminEventManager.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default AdminEventManager; 